import { useLayoutEffect, useMemo, useState } from 'react';
import { usePortfolio } from '../_Hooks/usePortfolio';
import { useSaleProjects } from '../_Hooks/useSaleProjects';
import { PortfolioNavbar } from '../_UI/PortfolioNavbar';
import { HeroSection } from '../_UI/HeroSection';
import { ProfileSpotlight } from '../_UI/ProfileSpotlight';
import { AboutSection } from '../_UI/AboutSection';
import { ProjectsSection } from '../_UI/ProjectsSection';
import { SaleProjectsSection } from '../_UI/SaleProjectsSection';
import { ExperienceSection } from '../_UI/ExperienceSection';
import { BlogSection } from '../_UI/BlogSection';
import { PortfolioFooter } from '../_UI/PortfolioFooter';
import { BlogArticleModal } from '../_UI/BlogArticleModal';
import '../_UI/_Styles/Portfolio.css';

const hasMeaningfulValue = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    return String(value).trim() !== '';
};

const singletonOrNull = (value) => {
    if (!value || typeof value !== 'object') return null;
    const entries = Object.entries(value).filter(([key]) => !['id', 'created_at', 'updated_at'].includes(key));
    return entries.some(([, current]) => hasMeaningfulValue(current)) ? value : null;
};

const collectionOrNull = (items = []) => {
    const rows = (Array.isArray(items) ? items : [])
        .filter((item) => item?.active !== false && Number(item?.active ?? 1) !== 0)
        .sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0));
    return rows.length ? rows : null;
};

export const PortfolioViewMain = () => {
    const { data, isLoading, error, reload } = usePortfolio();
    const { projects: saleProjects, submitOrder: submitSaleOrder } = useSaleProjects();
    const [selectedPost, setSelectedPost] = useState(null);

    useLayoutEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        html.classList.add('PB__document');
        body.classList.add('PB__body');
        return () => {
            html.classList.remove('PB__document', 'PB__modal_open');
            body.classList.remove('PB__body', 'PB__modal_open');
        };
    }, []);

    useLayoutEffect(() => {
        if (!selectedPost) return undefined;
        const html = document.documentElement;
        const body = document.body;
        const onKey = (event) => event.key === 'Escape' && setSelectedPost(null);
        html.classList.add('PB__modal_open');
        body.classList.add('PB__modal_open');
        window.addEventListener('keydown', onKey);
        return () => {
            html.classList.remove('PB__modal_open');
            body.classList.remove('PB__modal_open');
            window.removeEventListener('keydown', onKey);
        };
    }, [selectedPost]);

    const visible = useMemo(() => {
        if (!data) return null;
        return {
            profile: singletonOrNull(data.profile),
            about: singletonOrNull(data.about),
            stats: collectionOrNull(data.stats),
            skills: collectionOrNull(data.skills),
            projects: collectionOrNull(data.projects),
            experience: collectionOrNull(data.experience),
            blog: collectionOrNull(data.blog),
            socials: collectionOrNull(data.socials),
        };
    }, [data]);

    if (isLoading) {
        return (
            <div className="PB__state_screen">
                <span className="PB__loader" />
                <strong>Cargando portfolio</strong>
                <small>Preparando proyectos, trayectoria y publicaciones.</small>
            </div>
        );
    }

    if (error || !visible) {
        return (
            <div className="PB__state_screen PB__state_screen--error">
                <span className="PB__state_icon">!</span>
                <h1>No pude cargar el portfolio</h1>
                <p>{error?.message || 'Revisa que el backend PHP este ejecutandose en el puerto 4000.'}</p>
                <button type="button" className="PB__button PB__button--primary" onClick={reload}>Reintentar</button>
            </div>
        );
    }

    const hasAbout = !!(visible.about || visible.skills);
    const hasProjects = !!visible.projects;
    const hasSaleProjects = saleProjects.length > 0;
    const hasExperience = !!visible.experience;
    const hasBlog = !!visible.blog;

    return (
        <div className="PB__portfolio">
            <PortfolioNavbar
                profile={visible.profile || {}}
                hasAbout={hasAbout}
                hasProjects={hasProjects}
                hasSaleProjects={hasSaleProjects}
                hasExperience={hasExperience}
                hasBlog={hasBlog}
            />
            <main>
                <HeroSection profile={visible.profile || {}} stats={visible.stats || []} hasProjects={hasProjects} />
                {visible.profile && <ProfileSpotlight profile={visible.profile} socials={visible.socials || []} />}
                {hasAbout && <AboutSection about={visible.about || {}} skills={visible.skills || []} />}
                {hasProjects && <ProjectsSection projects={visible.projects} />}
                {hasSaleProjects && <SaleProjectsSection projects={saleProjects} onSubmitOrder={submitSaleOrder} />}
                {hasExperience && <ExperienceSection experience={visible.experience} />}
                {hasBlog && <BlogSection posts={visible.blog} onOpen={setSelectedPost} />}
            </main>
            <PortfolioFooter profile={visible.profile || {}} socials={visible.socials || []} />
            {selectedPost && <BlogArticleModal post={selectedPost} onClose={() => setSelectedPost(null)} />}
        </div>
    );
};
